import { useLocalizedStringFormatter } from '@coursera/cds-common';

import ButtonBase from '@core/ButtonBase';
import { useTableContext } from '@core/table/features/TableContext';
import i18nMessages from '@core/table/i18n';
import type { Column } from '@core/table/types';
import Typography from '@core/Typography';
import VisuallyHidden from '@core/VisuallyHidden';

import SortIcon from './SortIcon';
import { classes } from './styles/getTableHeadCellCss';

export type Props<
  Row extends Record<string, any>,
  K extends keyof Row & string
> = Pick<Column<Row, K>, 'label' | 'accessor' | 'sortable' | 'aria-label'>;

const SortButton = <
  Row extends Record<string, any>,
  K extends keyof Row & string
>(
  props: Props<Row, K>
) => {
  const { label, accessor, sortable, 'aria-label': ariaLabel } = props;

  const { handleSortClick, sort, id } = useTableContext();

  const handleSorting = () => {
    if (handleSortClick) {
      handleSortClick(accessor);
    }
  };

  const stringFormatter = useLocalizedStringFormatter(i18nMessages);
  const localizedAscending = stringFormatter.format('ascending');
  const localizedDescending = stringFormatter.format('descending');

  const sortDirection = accessor !== sort.key ? null : sort.direction;

  const localizedSortDirection =
    sortDirection === 'ascending' ? localizedAscending : localizedDescending;
  const localizedReverseSortDirection =
    sortDirection === 'ascending' ? localizedDescending : localizedAscending;

  const ariaId = `${id}-${accessor}-description`;
  const accessibleLabel: string = (ariaLabel || label)!;

  const ariaDescription: string =
    sortDirection === null
      ? stringFormatter.format('activate_to_sort')
      : stringFormatter.format('current_sort_status', {
          accessibleLabel,
          localizedSortDirection,
          localizedReverseSortDirection,
        });

  return (
    <ButtonBase
      aria-describedby={ariaId}
      aria-label={accessibleLabel}
      onClick={handleSorting}
    >
      <VisuallyHidden aria-hidden id={ariaId}>
        {ariaDescription}
      </VisuallyHidden>
      {label && (
        <Typography aria-hidden color="supportText">
          <strong>{label}</strong>
        </Typography>
      )}

      <span aria-hidden className={classes.iconWrapper}>
        <SortIcon accessor={accessor} sortable={sortable} />
      </span>
    </ButtonBase>
  );
};

export default SortButton;
